import React, { useState, useEffect } from "react";
import { CssBaseline, ThemeProvider, createTheme, CircularProgress } from "@mui/material";
import { BrowserRouter, Routes, Route, useParams, useNavigate, useSearchParams } from "react-router-dom";
import TestTargetManager from "./components/TestTargetManager";
import LoginModal from "./components/LoginModal";
import { SSTFApiClient } from "./services/api";
import {
  mergeApiConfig,
  ApiConfig,
} from "./config/apiConfig";
import { ToastProvider } from "./contexts/ToastContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TTM_TOAST_SNACKBAR_Z_INDEX } from "./constants/uiZIndex";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
  zIndex: {
    snackbar: TTM_TOAST_SNACKBAR_Z_INDEX,
  },
});

// ルーティング用のラッパーコンポーネント
const RoutedTestTargetManager: React.FC<{
  apiConfig: ApiConfig;
  apiClient: SSTFApiClient | null;
  onApiConfigChange: (config: ApiConfig) => void;
  onApiClientChange: (client: SSTFApiClient | null) => void;
  showApiConfigDialog: boolean;
  onApiConfigComplete: () => void;
}> = ({
  apiConfig,
  apiClient,
  onApiConfigChange,
  onApiClientChange,
  showApiConfigDialog,
  onApiConfigComplete,
}) => {
  const { workspaceId, projectId } = useParams<Record<string, string>>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetId = searchParams.get("targetId") || undefined;
  const usecaseId = searchParams.get("usecaseId") || undefined;
  const editMode = searchParams.get("edit") === "true";
  
  // URLからworkspaceName/projectNameを取得（query paramsから）
  const workspaceName = searchParams.get("workspaceName") || undefined;
  const projectName = searchParams.get("projectName") || undefined;

  // URLからworkspaceId/projectIdを取得（なければローカルストレージから）
  const effectiveWorkspaceId = workspaceId || localStorage.getItem("testTargetManager_workspaceId") || "";
  const effectiveProjectId = projectId || localStorage.getItem("testTargetManager_projectId") || "";

  // ワークスペース/プロジェクトIDの変更ハンドラー
  const handleWorkspaceIdChange = (newWorkspaceId: string) => {
    localStorage.setItem("testTargetManager_workspaceId", newWorkspaceId);
    // プロジェクトIDはリセット
    localStorage.removeItem("testTargetManager_projectId");
    // URLを更新（プロジェクト未選択状態）
    const paramsString = searchParams.toString();
    if (newWorkspaceId) {
      navigate(`/${newWorkspaceId}${paramsString ? `?${paramsString}` : ""}`);
    } else {
      navigate(paramsString ? `/?${paramsString}` : "/");
    }
  };

  const handleProjectIdChange = (newProjectId: string) => {
    localStorage.setItem("testTargetManager_projectId", newProjectId);
    // URLを更新
    const paramsString = searchParams.toString();
    if (effectiveWorkspaceId && newProjectId) {
      navigate(`/${effectiveWorkspaceId}/${newProjectId}${paramsString ? `?${paramsString}` : ""}`);
    }
  };

  // ダイアログを閉じる（URLからダイアログパラメータを削除）
  const handleCloseDialog = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("targetId");
    newParams.delete("usecaseId");
    newParams.delete("edit");
    setSearchParams(newParams);
  };

  return (
    <TestTargetManager
      apiConfig={apiConfig}
      apiClient={apiClient}
      onApiConfigChange={onApiConfigChange}
      onApiClientChange={onApiClientChange}
      workspaceId={effectiveWorkspaceId}
      projectId={effectiveProjectId}
      workspaceName={workspaceName}
      projectName={projectName}
      onWorkspaceIdChange={handleWorkspaceIdChange}
      onProjectIdChange={handleProjectIdChange}
      showApiConfigDialog={showApiConfigDialog}
      onApiConfigComplete={onApiConfigComplete}
      urlTargetId={targetId}
      urlUsecaseId={usecaseId}
      urlEditMode={editMode}
      onCloseDialog={handleCloseDialog}
    />
  );
};

// メインアプリケーションコンポーネント（Auth0認証内）
const AppContent: React.FC = () => {
  // API設定の状態管理
  const [apiConfig, setApiConfig] = useState<ApiConfig>(mergeApiConfig());
  const [apiClient, setApiClient] = useState<SSTFApiClient | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showApiConfigAfterLogin, setShowApiConfigAfterLogin] = useState(false);

  const { isAuthenticated, isLoading, user } = useAuth();

  // API設定の初期化
  useEffect(() => {
    console.log("API設定を初期化中...");
    const config = mergeApiConfig();
    console.log("読み込まれた設定:", config);
    setApiConfig(config);
  }, []);

  useEffect(() => {
    const clearedKey = "testTargetManager_initialClearDone";
    const suppressKey = "testTargetManager_suppressAutoConfigDialog";
    const alreadyCleared = sessionStorage.getItem(clearedKey);
    if (!alreadyCleared) {
      sessionStorage.setItem(clearedKey, "true");
      sessionStorage.setItem(suppressKey, "true");
      localStorage.removeItem("testTargetManager_workspaceId");
      localStorage.removeItem("testTargetManager_projectId");
      window.location.reload();
    }
  }, []);

  // 認証状態の監視
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        setShowLoginModal(true);
        setShowApiConfigAfterLogin(false);
        setApiClient(null);
      } else {
        setShowLoginModal(false);
        console.log("ユーザーが認証されました:", user);

        // 認証成功時にAPIクライアントを初期化
        if (apiConfig.isEnabled) {
          console.log("APIクライアントを初期化中...");
          const client = new SSTFApiClient(
            apiConfig.sstfBaseUrl,
            apiConfig.graphqlEndpoint,
            true
          );
          setApiClient(client);
          console.log("APIクライアントが初期化されました:", client);
        }

        // 認証成功後、ワークスペース/プロジェクトが未選択の場合はAPI設定を表示
        const savedWorkspaceId = localStorage.getItem("testTargetManager_workspaceId");
        const savedProjectId = localStorage.getItem("testTargetManager_projectId");
        const suppressKey = "testTargetManager_suppressAutoConfigDialog";
        const suppressAutoDialog = sessionStorage.getItem(suppressKey) === "true";
        if (!savedWorkspaceId || !savedProjectId) {
          if (suppressAutoDialog) {
            console.log("自動設定ダイアログが抑制されています");
          } else {
            console.log("ワークスペース/プロジェクトが未選択のため、API設定を表示");
            setShowApiConfigAfterLogin(true);
          }
        }
      }
    }
  }, [isAuthenticated, isLoading, user, apiConfig]);

  // APIクライアントがリセットされた際の処理
  const handleApiClientChange = (client: SSTFApiClient | null) => {
    setApiClient(client);
    if (client === null && apiClient !== null) {
      console.log("APIクライアントがリセットされました。");
    }
  };

  // API設定完了時の処理
  const handleApiConfigComplete = () => {
    setShowApiConfigAfterLogin(false);
    console.log("API設定が完了しました");
  };

  // ローディング中の表示
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <CircularProgress size={40} />
          <div>認証状態を確認中...</div>
        </div>
      </ThemeProvider>
    );
  }

  // 共通のプロパティ
  const managerProps = {
    apiConfig,
    apiClient,
    onApiConfigChange: setApiConfig,
    onApiClientChange: handleApiClientChange,
    showApiConfigDialog: showApiConfigAfterLogin,
    onApiConfigComplete: handleApiConfigComplete,
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => {}}
          onLoginSuccess={() => setShowLoginModal(false)}
        />
        {isAuthenticated && (
          <Routes>
            {/* プロジェクト選択済み */}
            <Route
              path="/:workspaceId/:projectId"
              element={<RoutedTestTargetManager {...managerProps} />}
            />
            {/* ワークスペースのみ選択 */}
            <Route
              path="/:workspaceId"
              element={<RoutedTestTargetManager {...managerProps} />}
            />
            {/* ルートパス */}
            <Route
              path="/"
              element={<RoutedTestTargetManager {...managerProps} />}
            />
          </Routes>
        )}
      </ToastProvider>
    </ThemeProvider>
  );
};

// メインAppコンポーネント（AuthProviderとBrowserRouterでラップ）
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;